import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { LabelComponent } from '../../shared/components/form/label/label.component';
import { InputFieldComponent } from '../../shared/components/form/input/input-field.component';
import { UbigeoPickerComponent } from '../../shared/components/form/ubigeo-picker/ubigeo-picker.component';
import { NotificationService } from '../../shared/components/ui/notification/notification.service';

import { MeCompanyService } from '../../services/me-company.service';
import { UbigeoService } from '../../services/ubigeo.service';
import { ExchangeRateService } from '../../services/exchange-rate.service';
import { SidebarRefreshService } from '../../services/sidebar-refresh.service';

import { CompanyResponse } from '../../dto/company.response';
import { CompanyRequest } from '../../dto/company.request';
import { UbigeoResponse } from '../../dto/ubigeo.response';
import { ExchangeRateResponse } from '../../dto/exchange-rate.response';

type CompanyTab = 'general' | 'sunat' | 'certificate';

@Component({
    selector: 'app-configuration',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        LabelComponent,
        InputFieldComponent,
        UbigeoPickerComponent,
    ],
    templateUrl: './configuration.component.html',
})
export class ConfigurationComponent implements OnInit {

    // ── COMPANY ───────────────────────────────────────────────────
    loadingCompany = false;
    savingCompany = false;
    companyTab: CompanyTab = 'general';
    companyFormSubmitted = false;

    ruc = '';
    businessName = '';
    tradeName = '';
    address = '';
    ubigeo = '';
    phone = '';
    email = '';
    website = '';
    logoUrl = '';
    logoFile: File | null = null;
    ubigDepartment = '';
    ubigProvince = '';
    ubigDistrict = '';
    sunatEstablishmentCode = '';
    sunatAmazoniaLaw = false;
    sunatProduction = false;
    sunatSecondaryUser = '';
    sunatSecondaryUserPassword = '';
    sunatGuideId = '';
    sunatGuidePassword = '';
    sunatDetractionAccount = '';
    pfxFile: File | null = null;
    pfxPassword = '';
    sunatCertificateLoaded: boolean | null = null;
    createdAt = '';

    allUbigeos: UbigeoResponse[] = [];
    loadingUbigeos = false;

    get companyFormError(): string {
        if (!this.businessName.trim()) return 'Razón social requerida';
        if (!this.address.trim()) return 'Dirección requerida';
        if (!this.ubigeo.trim()) return 'Ubigeo requerido';
        return '';
    }

    get sunatFormError(): string {
        if (!this.sunatEstablishmentCode.trim()) return 'Código de establecimiento requerido';
        if (this.sunatProduction && this.sunatSecondaryUser.toUpperCase().includes('MODDATOS'))
            return 'En producción el usuario SUNAT no puede contener "MODDATOS"';
        return '';
    }

    private removeAccents(str: string): string {
        return str.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase();
    }

    onUbigeoChange(code: string): void {
        this.ubigeo = code;
        const u = this.allUbigeos.find(u => u.ubigeo === code);
        this.ubigDepartment = u ? this.removeAccents(u.department) : '';
        this.ubigProvince = u ? this.removeAccents(u.province) : '';
        this.ubigDistrict = u ? this.removeAccents(u.district) : '';
    }

    onLogoFileChange(event: Event): void {
        this.logoFile = (event.target as HTMLInputElement).files?.[0] ?? null;
    }

    onPfxFileChange(event: Event): void {
        this.pfxFile = (event.target as HTMLInputElement).files?.[0] ?? null;
    }

    // ── EXCHANGE RATE ─────────────────────────────────────────────
    todayRate?: ExchangeRateResponse;
    tcTodayLoading = false;

    constructor(
        private meCompanyService: MeCompanyService,
        private ubigeoService: UbigeoService,
        private exchangeRateService: ExchangeRateService,
        private notify: NotificationService,
        private sidebarRefresh: SidebarRefreshService,
    ) {}

    ngOnInit(): void {
        this.loadCompany();
        this.loadUbigeos();
        this.exchangeRateService.getToday().subscribe({
            next: res => { this.todayRate = res.data ?? undefined; },
            error: () => {}
        });
    }

    private loadUbigeos(): void {
        this.loadingUbigeos = true;
        this.ubigeoService.listActive().subscribe({
            next: res => { this.allUbigeos = res.data ?? []; this.loadingUbigeos = false; },
            error: () => { this.loadingUbigeos = false; }
        });
    }

    loadCompany(): void {
        this.loadingCompany = true;
        this.meCompanyService.get().subscribe({
            next: res => { this.loadingCompany = false; this.populateForm(res.data); },
            error: err => { this.loadingCompany = false; this.notify.error(err?.error?.message ?? 'Error al cargar datos de la empresa'); }
        });
    }

    private populateForm(c: CompanyResponse): void {
        this.ruc = c.ruc;
        this.businessName = c.businessName;
        this.tradeName = c.tradeName ?? '';
        this.address = c.address ?? '';
        this.ubigeo = c.ubigeo ?? '';
        this.phone = c.phone ?? '';
        this.email = c.email ?? '';
        this.website = c.website ?? '';
        this.logoUrl = c.logoUrl ?? '';
        this.sunatEstablishmentCode = c.sunatEstablishmentCode ?? '';
        this.sunatAmazoniaLaw = c.sunatAmazoniaLaw ?? false;
        this.sunatProduction = c.sunatProduction ?? false;
        this.sunatSecondaryUser = c.sunatSecondaryUser ?? '';
        this.sunatGuideId = c.sunatGuideId ?? '';
        this.ubigDepartment = c.ubigDepartment ? this.removeAccents(c.ubigDepartment) : '';
        this.ubigProvince = c.ubigProvince ? this.removeAccents(c.ubigProvince) : '';
        this.ubigDistrict = c.ubigDistrict ? this.removeAccents(c.ubigDistrict) : '';
        this.sunatDetractionAccount = c.sunatDetractionAccount ?? '';
        this.sunatCertificateLoaded = c.sunatCertificateLoaded ?? null;
        this.createdAt = c.createdAt ?? '';
        this.sunatSecondaryUserPassword = c.sunatSecondaryUserPassword ?? '';
        this.sunatGuidePassword = c.sunatGuidePassword ?? '';
        this.pfxFile = null;
        this.pfxPassword = '';
        this.logoFile = null;
    }

    saveCompany(): void {
        this.companyFormSubmitted = true;
        if (this.companyFormError) { this.companyTab = 'general'; return; }
        if (this.sunatFormError) { this.companyTab = 'sunat'; return; }
        this.savingCompany = true;

        const req: CompanyRequest = {
            ruc: this.ruc.trim(),
            businessName: this.businessName.trim(),
            tradeName: this.tradeName.trim() || undefined,
            address: this.address.trim(),
            ubigeo: this.ubigeo.trim(),
            phone: this.phone.trim() || undefined,
            email: this.email.trim() || undefined,
            website: this.website.trim() || undefined,
            logoUrl: this.logoUrl.trim() || undefined,
            sunatEstablishmentCode: this.sunatEstablishmentCode.trim(),
            sunatAmazoniaLaw: this.sunatAmazoniaLaw,
            sunatProduction: this.sunatProduction,
            sunatSecondaryUser: this.sunatSecondaryUser.trim() || undefined,
            sunatSecondaryUserPassword: this.sunatSecondaryUserPassword.trim() || undefined,
            sunatGuideId: this.sunatGuideId.trim() || undefined,
            sunatGuidePassword: this.sunatGuidePassword.trim() || undefined,
            ubigDepartment: this.ubigDepartment.trim() || undefined,
            ubigProvince: this.ubigProvince.trim() || undefined,
            ubigDistrict: this.ubigDistrict.trim() || undefined,
            sunatDetractionAccount: this.sunatDetractionAccount.trim() || undefined,
        };

        this.meCompanyService.update(req, this.logoFile, this.pfxFile, this.pfxPassword.trim() || null).subscribe({
            next: res => {
                this.savingCompany = false;
                this.companyFormSubmitted = false;
                this.notify.success(res.message ?? 'Empresa actualizada correctamente');
                this.populateForm(res.data);
                this.sidebarRefresh.refreshLogo(res.data?.logoUrl ?? '');
            },
            error: err => {
                this.savingCompany = false;
                this.notify.error(err?.error?.message ?? 'Error al guardar datos de la empresa');
            }
        });
    }

    // ── EXCHANGE RATE ─────────────────────────────────────────────
    updateTodayRate(): void {
        this.tcTodayLoading = true;
        const today = this.localToday();
        this.exchangeRateService.bulkImport(today, today).subscribe({
            next: res => {
                this.tcTodayLoading = false;
                this.todayRate = res.data?.[0] ?? this.todayRate;
                this.notify.success(res?.message ?? 'Tipo de cambio del día actualizado', 'Tipo de cambio');
            },
            error: err => { this.tcTodayLoading = false; this.notify.error(err?.error?.message ?? 'No se pudo actualizar el tipo de cambio'); },
        });
    }

    private localToday(): string {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
}
